import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Trash2Icon } from 'lucide-react';
import { isExpired, isExpiringWithin } from './pharmacyUtils';
export function ExpiredMedicines() {
  const {
    medicineBatches,
    medicines,
    recordStockMovement
  } = useData();
  const [message, setMessage] = useState('');
  const expired = medicineBatches.filter(isExpired);
  const expiring = medicineBatches.filter(batch => isExpiringWithin(batch, 90));
  const dispose = (batchId: string) => {
    const batch = medicineBatches.find(item => item.id === batchId);
    if (!batch || !confirm(`Dispose ${batch.quantity} units from batch ${batch.batchNumber}?`)) return;
    const result = recordStockMovement({
      medicineId: batch.medicineId,
      batchId: batch.id,
      quantityDelta: -batch.quantity,
      type: 'Expired Disposal',
      referenceType: 'Expiry',
      referenceId: batch.id,
      notes: `Expired batch ${batch.batchNumber} disposed.`
    });
    setMessage(result.ok ? 'Expired stock disposed and recorded in history.' : result.error || 'Unable to dispose stock.');
  };
  const rows = (items: typeof medicineBatches, action = false) => items.map(batch => {
    const medicine = medicines.find(item => item.id === batch.medicineId);
    return <tr key={batch.id} data-id="element-4058">
          <td className="px-5 py-3.5 font-semibold text-slate-900" data-id="element-4059">
            {medicine?.name || 'Unknown medicine'}
          </td>
          <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4060">
            {batch.batchNumber}
          </td>
          <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4061">
            {new Date(`${batch.expiryDate}T00:00:00`).toLocaleDateString()}
          </td>
          <td className="px-5 py-3.5 text-sm font-semibold text-slate-900" data-id="element-4062">
            {batch.quantity}
          </td>
          {action && <td className="px-5 py-3.5 text-right" data-id="element-4063">
              <Button size="sm" variant="danger" onClick={() => dispose(batch.id)} data-id="element-4064">
                <Trash2Icon className="mr-1.5 h-4 w-4" data-id="element-4065" /> Dispose
              </Button>
            </td>}
        </tr>;
  });
  return <div className="space-y-6" data-id="element-4066">
      <div data-id="element-4067">
        <h2 className="text-xl font-bold text-slate-950" data-id="element-4068">Expired Medicines</h2>
        <p className="mt-1 text-sm text-slate-500" data-id="element-4069">
          Batch expiry is live; disposal automatically updates stock and
          history.
        </p>
      </div>
      {message && <p role="status" className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700" data-id="element-4070">
          {message}
        </p>}
      <BatchTable title="Expired batches" items={expired} rows={rows(expired, true)} action data-id="element-4071" />
      <BatchTable title="Expiring within 90 days" items={expiring} rows={rows(expiring)} data-id="element-4072" />
    </div>;
}
function BatchTable({
  title,
  items,
  rows,
  action
}: {
  title: string;
  items: unknown[];
  rows: React.ReactNode;
  action?: boolean;
}) {
  return <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4073">
      <div className="border-b border-slate-100 px-5 py-4" data-id="element-4074">
        <h3 className="font-bold text-slate-900" data-id="element-4075">{title}</h3>
      </div>
      <div className="overflow-x-auto" data-id="element-4076">
        <table className="min-w-full divide-y divide-slate-100" data-id="element-4077">
          <thead className="bg-slate-50" data-id="element-4078">
            <tr data-id="element-4079">
              {['Medicine', 'Batch', 'Expiry date', 'Quantity', ...(action ? ['Action'] : [])].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-4080">
                  {heading}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-4081">
            {items.length ? rows : <tr data-id="element-4082">
                <td colSpan={action ? 5 : 4} className="px-5 py-12 text-center text-sm text-slate-500" data-id="element-4083">
                  No batches in this category.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </section>;
}